import {Flag, FlagProps, Tooltip} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {ConsentResponseBody, ConsentStatus, ConsentSubject} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {DOT_CHARACTER, EMPTY_PLACEHOLDER, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {useBranches} from '../../../hooks/useBranches';
import {
  ContactPersonConsentStatus,
  getContactPersonConsentStatus,
} from '../utils/getContactPersonConsentStatus';

interface ContactPersonConsentFlagProps extends TestIdProps {
  contactPersonId: string;
  consents: ConsentResponseBody[];
}

export function ContactPersonConsentFlag(props: ContactPersonConsentFlagProps) {
  const tooltipConsents = props.consents.filter(
    (consent) => isNil(consent.contactPersonId) || consent.contactPersonId === props.contactPersonId
  );

  return (
    <Tooltip
      label={i18n.t('entity.gdpr.labels.consents')}
      description={<ConsentList consents={tooltipConsents} />}
      isDisabled={tooltipConsents.length === 0}
      hasAutoWidth
      data-testid={suffixTestId('consentTooltip', props)}
    >
      <Flag
        size="small"
        isSubtle
        {...getFlag(getContactPersonConsentStatus(props.consents, props.contactPersonId))}
        data-testid={suffixTestId('consentFlag', props)}
      />
    </Tooltip>
  );
}

const getFlag = (status: ContactPersonConsentStatus) =>
  match(status)
    .with('agreed', (): FlagProps => ({
      colorScheme: 'green',
      label: i18n.t('entity.gdpr.labels.contactPersonConsent.agreed'),
    }))
    .with('refused', (): FlagProps => ({
      colorScheme: 'red',
      label: i18n.t('entity.gdpr.labels.contactPersonConsent.refused'),
    }))
    .with('none', (): FlagProps => ({
      colorScheme: 'neutral',
      label: i18n.t('entity.gdpr.labels.contactPersonConsent.none'),
    }))
    .exhaustive();

const getStatus = (status: ConsentStatus) =>
  match(status)
    .with(ConsentStatus.AGREED, () => i18n.t('entity.gdpr.labels.constent.status.agreed'))
    .with(ConsentStatus.EXPIRED, () => i18n.t('entity.gdpr.labels.constent.status.expired'))
    .with(ConsentStatus.REJECTED, () => i18n.t('entity.gdpr.labels.constent.status.rejected'))
    .with(ConsentStatus.WITHDRAWN, () => i18n.t('entity.gdpr.labels.constent.status.withdrawn'))
    .exhaustive();

interface ConsentListProps {
  consents: ConsentResponseBody[];
}

function ConsentList(props: ConsentListProps) {
  const formatDateTime = useDateTimeFormatter();
  const {getBranchById} = useBranches();

  const getRecipient = (consent: ConsentResponseBody) =>
    [
      match(consent.subject)
        .with(ConsentSubject.TENANT, () => i18n.t('entity.gdpr.labels.consentType.subjects.tenant'))
        .with(
          ConsentSubject.BRANCH,
          () => getBranchById(consent.branchId ?? '')?.marketingName ?? EMPTY_PLACEHOLDER
        )
        .otherwise(() => null),
      isNil(consent.recipient)
        ? null
        : (consent.recipient.name ?? i18n.t('entity.gdpr.labels.consentType.subjects.third_party')),
    ]
      .filter(isNotNil)
      .join(', ');

  const getValidity = (consent: ConsentResponseBody) =>
    match(consent)
      .with({status: ConsentStatus.REJECTED}, () => null)
      .with(
        {validUntil: Pattern.string},
        ({validUntil}) =>
          `${i18n.t('entity.gdpr.labels.consent.validUntil')}: ${formatDateTime('dateShort', parseDate(validUntil))}`
      )
      .otherwise(() => i18n.t('entity.gdpr.labels.consentType.durations.lifetime'));

  return (
    <VStack spacing={2}>
      {props.consents.map((consent) => (
        <VStack key={consent.id}>
          <Text size="xSmall" color="white" alternative>
            {consent.name}
          </Text>
          <Text size="xSmall" color="white">
            {getRecipient(consent)}
          </Text>
          <Text size="xSmall" color="white">
            {[
              getStatus(consent.status),
              getValidity(consent),
              consent.channels.map((channel) => i18n.t(`general.labels.${channel}`)).join(', '),
            ]
              .filter(isNotNil)
              .join(` ${DOT_CHARACTER} `)}
          </Text>
          <Show when={isNil(consent.contactPersonId)}>
            <Text size="xSmall" color="white">
              {i18n.t('entity.gdpr.labels.consent.validForWholeCustomer')}
            </Text>
          </Show>
        </VStack>
      ))}
    </VStack>
  );
}
